// Rideaux verticaux : une « jupe » qui relie une annotation élevée au sol, pour
// rendre sa hauteur visible en 3D. Géométrie = bande de quads verticaux (sol →
// altitude de l'élément) le long du tracé. Pas de courbe ni de repère de Frenet
// (contrairement à l'ancien tube) → robuste, aucune torsion possible.
//
// Une seule couche WebGL custom Mapbox héberge tous les rideaux (un renderer
// three.js partageant le contexte GL). On bake l'origine (Mercator du 1er point)
// dans la position du mesh en double précision et on construit la géométrie en
// delta Mercator → pas de jitter float32.
import * as THREE from 'three'
import mapboxgl from 'mapbox-gl'
import { getRoot } from '/@/root'

const LAYER_ID = '__curtains__'
const DEFAULT_COLOR = '#2D8CF0'

interface CurtainEntry {
  mesh: THREE.Mesh
  material: THREE.MeshBasicMaterial
  geometry: THREE.BufferGeometry
}

let scene: THREE.Scene | null = null
let camera: THREE.Camera | null = null
let renderer: THREE.WebGLRenderer | null = null
const curtains = new Map<string, CurtainEntry>()

function getMap (): mapboxgl.Map | null {
  return (getRoot() as any)?.$map || null
}

function ensureLayer (map: mapboxgl.Map): void {
  if (!scene) {
    scene = new THREE.Scene()
    camera = new THREE.Camera()
  }
  if (map.getLayer(LAYER_ID)) return
  const layer: any = {
    id: LAYER_ID,
    type: 'custom',
    renderingMode: '3d',
    onAdd (_map: mapboxgl.Map, gl: WebGLRenderingContext) {
      renderer = new THREE.WebGLRenderer({ canvas: _map.getCanvas(), context: gl as any, antialias: false })
      renderer.autoClear = false
    },
    render (_gl: WebGLRenderingContext, matrix: number[]) {
      if (!renderer || !scene || !camera) return
      // Aucun rideau : on ne touche pas au renderer → l'état GL de Mapbox reste
      // intact et la carte peut s'immobiliser (sinon écran noir à l'arrêt).
      if (curtains.size === 0) return
      camera.projectionMatrix = new THREE.Matrix4().fromArray(matrix as number[])
      renderer.resetState()
      renderer.render(scene, camera)
      map.triggerRepaint()
    },
  }
  map.addLayer(layer)
}

function haversine (a: number[], b: number[]): number {
  const R = 6371000
  const dLat = (b[1] - a[1]) * Math.PI / 180
  const dLng = (b[0] - a[0]) * Math.PI / 180
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
}

// Densifie (~tous les 15 m) pour que le bas du rideau épouse le relief.
function densify2d (path2d: number[][], step = 15): number[][] {
  if (path2d.length < 2) return path2d
  const out: number[][] = []
  for (let i = 0; i < path2d.length - 1; i++) {
    const a = path2d[i]
    const b = path2d[i + 1]
    const n = Math.max(1, Math.min(2000, Math.round(Math.max(haversine(a, b), 0.001) / step)))
    for (let k = 0; k < n; k++) {
      const t = k / n
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
  }
  out.push(path2d[path2d.length - 1])
  return out
}

export interface CurtainOpts {
  // 'agl' : sommet du rideau = terrain + value ; 'msl' : sommet = value (altitude absolue).
  mode: 'agl' | 'msl'
  value: number
  color?: string
  opacity?: number
  // Largeur (m) du rideau pour un point unique (pin) → quad vertical type mât.
  poleWidthM?: number
}

function topAltAt (map: mapboxgl.Map, lng: number, lat: number, terr: number, o: CurtainOpts): number {
  return o.mode === 'msl' ? o.value : terr + o.value
}

function terrainAt (map: mapboxgl.Map, lng: number, lat: number): number {
  // Terrain *exagéré* : le rideau se cale sur le sol rendu et sur la ligne Mapbox.
  return ((map as any).queryTerrainElevation({ lng, lat }, { exaggerated: true }) ?? 0)
}

function buildCurtain (map: mapboxgl.Map, path2d: number[][], o: CurtainOpts): CurtainEntry | null {
  // Mât (pin) : un seul point → on fabrique 2 points décalés pour avoir une bande.
  let path = path2d
  if (path.length === 1) {
    const w = (o.poleWidthM ?? 1.5)
    const [lng, lat] = path[0]
    const dLng = w / (111320 * Math.cos(lat * Math.PI / 180))
    path = [[lng - dLng / 2, lat], [lng + dLng / 2, lat]]
  }
  const dense = densify2d(path)
  if (dense.length < 2) return null

  const bottomAlt = dense.map(p => terrainAt(map, p[0], p[1]))
  const topAlt = dense.map((p, i) => topAltAt(map, p[0], p[1], bottomAlt[i], o))

  const o0 = mapboxgl.MercatorCoordinate.fromLngLat([dense[0][0], dense[0][1]], bottomAlt[0])

  const positions: number[] = []
  const indices: number[] = []
  for (let i = 0; i < dense.length; i++) {
    const b = mapboxgl.MercatorCoordinate.fromLngLat([dense[i][0], dense[i][1]], bottomAlt[i])
    const t = mapboxgl.MercatorCoordinate.fromLngLat([dense[i][0], dense[i][1]], topAlt[i])
    positions.push(b.x - o0.x, b.y - o0.y, b.z - o0.z) // sommet bas (2*i)
    positions.push(t.x - o0.x, t.y - o0.y, t.z - o0.z) // sommet haut (2*i+1)
  }
  for (let i = 0; i < dense.length - 1; i++) {
    const b0 = 2 * i
    const t0 = 2 * i + 1
    const b1 = 2 * (i + 1)
    const t1 = 2 * (i + 1) + 1
    indices.push(b0, b1, t0, t0, b1, t1)
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setIndex(indices)
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(o.color || DEFAULT_COLOR),
    transparent: true,
    opacity: o.opacity ?? 0.25,
    side: THREE.DoubleSide,
    depthWrite: false,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.position.set(o0.x, o0.y, o0.z)
  return { mesh, material, geometry }
}

/** Crée / remplace le rideau d'un élément. `path2d` = [[lng,lat], …] (1 point = mât). */
export function setCurtain (id: string, path2d: number[][], opts: CurtainOpts): void {
  const map = getMap()
  if (!map || !path2d || path2d.length < 1) return
  removeCurtain(id)
  // Rien à dessiner si l'élément n'est pas élevé.
  if (opts.mode === 'agl' && !(opts.value > 0)) return
  ensureLayer(map)
  if (!scene) return
  const entry = buildCurtain(map, path2d, opts)
  if (!entry) return
  scene.add(entry.mesh)
  curtains.set(id, entry)
  map.triggerRepaint()
}

export function setCurtainColor (id: string, color?: string): void {
  const entry = curtains.get(id)
  if (!entry) return
  entry.material.color.set(color || DEFAULT_COLOR)
  getMap()?.triggerRepaint()
}

export function removeCurtain (id: string): void {
  const entry = curtains.get(id)
  if (!entry) return
  scene?.remove(entry.mesh)
  entry.geometry.dispose()
  entry.material.dispose()
  curtains.delete(id)
  getMap()?.triggerRepaint()
}

export function hasCurtain (id: string): boolean {
  return curtains.has(id)
}
